import React, {createContext, FC, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../../../_metronic/helpers'
import {LoadingIndicator} from '../../common/components/LoadingIndicator'
import CardLayout from '../../common/components/CardLayout'
import axios from '../../../helpers/axios'
import {RequestError} from '../../common/components/RequestError'
import {ApplicationConfiguration, Language, MenuEntry, MenuRoot} from './_models'
import {Http} from '../../../helpers/http'

type ApplicationConfigurationContextProps = {
  menu: MenuRoot | undefined
  config: ApplicationConfiguration | undefined
  languages: Language[] | undefined
  menuFlat: MenuEntry[] | undefined
}

const initApplicationConfigurationContextPropsState = {
  menu: undefined,
  config: undefined,
  languages: undefined,
  menuFlat: undefined,
}

const ApplicationConfigurationContext = createContext<ApplicationConfigurationContextProps>(
  initApplicationConfigurationContextPropsState
)

const useApplicationConfig = () => {
  return useContext(ApplicationConfigurationContext)
}

const ApplicationConfigurationProvider: FC<WithChildren> = ({children}) => {
  const [menu, setMenu] = useState<undefined | MenuRoot>(undefined)
  const [menuFlat, setMenuFlat] = useState<undefined | MenuEntry[]>(undefined)
  const [config, setConfig] = useState<undefined | ApplicationConfiguration>(undefined)
  const [languages, setLanguages] = useState<undefined | Language[]>(undefined)
  const [error, setError] = useState(false)

  const flattenMenu = (items: MenuEntry[]): MenuEntry[] => {
    const flattened = []

    for (const item of items) {
      flattened.push(item)

      if (item.items != null && item.items.length) {
        flattened.push(...flattenMenu(item.items))
      }
    }

    return flattened
  }

  const loadData = async () => {
    setError(false)
    try {
      const {data: menuData} = await Http.get<MenuRoot>('get_full_menu')
      const {data: applicationData} = await Http.get<ApplicationConfiguration>(
        'get_application_data'
      )
      const {data: languageData} = await Http.get<Language[]>('get_languages')
      setMenu(menuData)
      setConfig(applicationData)
      setLanguages(languageData)
      setMenuFlat(flattenMenu(menuData.items))
    } catch (e) {
      console.error(e)
      setError(true)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  if (error) {
    return <RequestError onRefresh={loadData} />
  }

  if (menu == null) {
    return (
      <CardLayout>
        <div className='d-flex align-content-center'>
          <LoadingIndicator /> <span className='ps-5 pt-1'>Loading Configuration...</span>
        </div>
      </CardLayout>
    )
  }

  return (
    <ApplicationConfigurationContext.Provider value={{menu, config, languages, menuFlat}}>
      {children}
    </ApplicationConfigurationContext.Provider>
  )
}

export {ApplicationConfigurationProvider, useApplicationConfig}
